import { useCallback, useEffect, useState } from "react";

// The takeoff engine's INTERFACE theme (light / dark) — the toolbars, dock, flyouts, windows and the Arch Takeoff
// tabs. A per-viewer display preference (localStorage), separate from the DRAWING's own light / dark view
// (utils/canvasTheme.js), which only inverts the plan. The page root carries data-theme and index.css redefines the
// color tokens under [data-theme="dark"]; nothing about the estimate changes.

export const UI_THEME_STORAGE_KEY = "cs.takeoff.uiTheme";
const EVENT = "cs:takeoff-theme-change";

export const readUiTheme = (storage = typeof localStorage !== "undefined" ? localStorage : null) => {
  try { return storage?.getItem(UI_THEME_STORAGE_KEY) === "dark" ? "dark" : "light"; } catch { return "light"; }
};
export const writeUiTheme = (theme, storage = typeof localStorage !== "undefined" ? localStorage : null) => {
  try { storage?.setItem(UI_THEME_STORAGE_KEY, theme === "dark" ? "dark" : "light"); } catch { /* a view preference only */ }
};
export const toggleUiTheme = (theme) => (theme === "dark" ? "light" : "dark");

/** [theme, setTheme, toggle] — shared by the page root (sets data-theme) and the toolbar button, so they stay in step. */
export function useTakeoffUiTheme() {
  const [theme, setThemeState] = useState(() => readUiTheme());
  useEffect(() => {
    const on = () => setThemeState(readUiTheme());
    // The canvas lives in its own browser window: the same-page event covers components in this window, and the
    // storage event (fired in the OTHER windows of the same origin) keeps the main page and the canvas window in step.
    const onStorage = (e) => { if (e.key === UI_THEME_STORAGE_KEY || e.key === null) on(); };
    window.addEventListener(EVENT, on);
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener(EVENT, on); window.removeEventListener("storage", onStorage); };
  }, []);
  const setTheme = useCallback((next) => { writeUiTheme(next); setThemeState(next === "dark" ? "dark" : "light"); window.dispatchEvent(new Event(EVENT)); }, []);
  const toggle = useCallback(() => setTheme(toggleUiTheme(readUiTheme())), [setTheme]);
  return [theme, setTheme, toggle];
}
